import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './primitives.css';

export interface PopoverProps {
  content: ReactNode;
  /** Single React element used as the trigger. */
  children: ReactElement;
  side?: 'bottom' | 'top';
  align?: 'start' | 'end' | 'center';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export function Popover({
  content,
  children,
  side = 'bottom',
  align = 'end',
  open: openProp,
  onOpenChange,
  className,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  // A bottom panel that would run off the window opens upward instead,
  // when there is room above.
  const [flipped, setFlipped] = useState(false);
  const effectiveSide = side === 'bottom' && flipped ? 'top' : side;

  useEffect(() => {
    if (!open) {
      setCoords(null);
      setFlipped(false);
      return;
    }
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const x =
      align === 'start'
        ? r.left
        : align === 'end'
        ? r.right
        : r.left + r.width / 2;
    const y = effectiveSide === 'bottom' ? r.bottom + 6 : r.top - 6;
    setCoords({ x, y });
  }, [open, effectiveSide, align]);

  useLayoutEffect(() => {
    if (!open || !coords || side !== 'bottom' || flipped) return;
    const panel = panelRef.current?.getBoundingClientRect();
    const r = triggerRef.current?.getBoundingClientRect();
    if (!panel || !r) return;
    const margin = 8;
    if (panel.bottom > window.innerHeight - margin && r.top - 6 - panel.height >= margin) {
      setFlipped(true);
    }
  }, [open, coords, side, flipped]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  const child = children as ReactElement<{
    ref?: (el: HTMLElement | null) => void;
    onClick?: (e: React.MouseEvent) => void;
  }>;

  const triggerEl = (() => {
    const next = {
      ref: (el: HTMLElement | null) => {
        triggerRef.current = el;
      },
      onClick: (e: React.MouseEvent) => {
        child.props.onClick?.(e);
        setOpen(!open);
      },
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { ...child, props: { ...child.props, ...next } } as any;
  })();

  return (
    <>
      {triggerEl}
      {open && coords &&
        createPortal(
          <div
            ref={panelRef}
            className={['lm-popover', className].filter(Boolean).join(' ')}
            style={{
              left: coords.x,
              top: coords.y,
              transform:
                effectiveSide === 'top'
                  ? align === 'end'
                    ? 'translate(-100%, -100%)'
                    : align === 'start'
                    ? 'translate(0, -100%)'
                    : 'translate(-50%, -100%)'
                  : align === 'end'
                  ? 'translate(-100%, 0)'
                  : align === 'start'
                  ? 'translate(0, 0)'
                  : 'translate(-50%, 0)',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
